import { getFirstPodcast } from "../private/api/getFirstPodcast";
import { getPreviewPodcast } from "../private/api/getPreviewPodcast";
import { getSignedUrl } from "../private/api/getSignedUrl";
import { getPagePodcasts } from "../private/api/getPagePodcasts";
import { getOtherPodcastList } from "../private/api/otherPodcasts";

const Audioplayer = {
  namespaced: true,

  state: {
    list: {},
    otherPodcasts: {},

    bufferedRanges: [],
    audioplayer: null,

    isPlaying: false,
    isLoading: false,
    volume: 1,
    speed: 1,
    currentTime: 0,

    activePodcastId: null,
    podcastPreviewText: null,

    isAudioplayerLoading: false,
    isAudioplayerReady: false,
    isStickyPanelOpened: false,
    isLoadingPodcastsList: false,

    currentAudioRequest: null,
    wasTimeRestoredFromStorage: false,
  },

  actions: {
    async initPlayer({ commit, dispatch }) {
      await dispatch('initFirstPodcast');
      await commit('SET_AUDIOPLAYER_API');

      navigator.mediaSession.setActionHandler('play', () => { dispatch('resumeAudio') });
      navigator.mediaSession.setActionHandler('pause', () => { dispatch('pauseAudio') });

      const savedVolume = localStorage.getItem('audio_volume');
      commit('SET_VOLUME', savedVolume !== null ? parseFloat(savedVolume) : 0.5);

      const savedSpeed = localStorage.getItem('audio_speed');
      commit('SET_SPEED', savedSpeed !== null ? parseFloat(savedSpeed) : 1);

      commit('SET_READY_AUDIOPLAYER_FLAG');
    },

    async initFirstPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getFirstPodcast();
      const podcast = data[0].podcast_atributes;
      commit('SET_NEW_AUDIO', podcast);
      commit('WRITE_ID_ACTIVE_PODCAST', podcast.id);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async initPreviewPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getPreviewPodcast();
      commit('SET_PREVIEW', data.title);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async activeCurrentPodcats({ commit, dispatch, state }) {
      commit('TOGGLE_STICKY_PANEL', true);
      commit('SET_LOADING_AUDIO_FLAG', true);
      try {
        const currentPodcast = state.list[state.activePodcastId];
        if (!currentPodcast?.signed_url) {
          await dispatch('useSignedUrlForAudio');
        }

        if (state.audioplayer.src !== currentPodcast.signed_url) {
          commit('SET_AUDIO_URL', currentPodcast.signed_url);

          await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Timeout loading audio')), 10000);
            const onCanPlay = () => {
              clearTimeout(timer);
              state.audioplayer.removeEventListener('canplay', onCanPlay);
              resolve();
            };
            state.audioplayer.addEventListener('canplay', onCanPlay);
            if (state.audioplayer.readyState > 0) onCanPlay();
          });

          if (
            !state.wasTimeRestoredFromStorage &&
            localStorage.getItem('last_audio_id') === String(state.activePodcastId)
          ) {
            const savedTime = parseFloat(localStorage.getItem('last_remember_time') || '0');
            if (!isNaN(savedTime)) {
              commit('SET_CURRENT_TIME', savedTime);
              state.audioplayer.currentTime = savedTime;
            }
            state.wasTimeRestoredFromStorage = true;
          } else {
            state.audioplayer.currentTime = state.currentTime;
          }
        }

        commit('SET_VOLUME', state.volume);
        commit('SET_SPEED', state.speed);
        commit('SET_CURRENT_TIME', state.currentTime);

        state.audioplayer.currentTime = state.currentTime;
        await state.audioplayer.play();
        commit('SET_PLAYING_FLAG', true);
      } catch (error) {
        console.error('Playback failed:', error);
        commit('SET_PLAYING_FLAG', false);
        throw error;
      } finally {
        commit('SET_LOADING_AUDIO_FLAG', false);
      }
    },

    async useSignedUrlForAudio({ state, commit }) {
      if (state.currentAudioRequest) {
        state.currentAudioRequest.abort?.();
      }

      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast || !currentPodcast.audio_file) return;

      const shouldRefreshLink = () => {
        if (!currentPodcast.signed_url) return true;
        if (!currentPodcast.signed_expires_at) return true;
        const expiresAt = new Date(currentPodcast.signed_expires_at);
        const refreshThreshold = new Date(expiresAt.getTime() - 600000);
        return new Date() > refreshThreshold;
      };

      if (!shouldRefreshLink()) return;

      const match = currentPodcast.audio_file.match(/\/([^\/?#]+)$/);
      const name = match[1];
      const request = getSignedUrl(name);
      commit('SET_AUDIO_REQUEST', request);

      try {
        const data = await request;
        const cleanSignedUrl = data.signed_url.replace(/\.sdn/, '');
        commit('UPDATE_PODCAST_SIGNED_URL', {
          id: state.activePodcastId,
          signed_url: cleanSignedUrl,
          expires_at: data.expires_at
        });
      } catch (error) {
        console.error('Failed to get signed URL:', error);
        throw error;
      }
    },

    async changeVolume({ commit }, volume) {
      commit('SET_VOLUME', volume);
      localStorage.setItem('audio_volume', volume);
    },

    async changeSpeed({ commit }, speed) {
      commit('SET_SPEED', speed);
      localStorage.setItem('audio_speed', speed);
    },

    async closeStickyPanel({ state, commit }, status) {
      commit('TOGGLE_STICKY_PANEL', status);
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
    },

    async playAudio({ state, commit, dispatch }, { id }) {
      if (state.isPlaying) {
        await dispatch('pauseAudio');
      }
      commit('SET_CURRENT_TIME', 0);
      commit('WRITE_ID_ACTIVE_PODCAST', id);
      state.wasTimeRestoredFromStorage = false;
      dispatch('activeCurrentPodcats');
    },

    async pauseAudio({ state, commit }) {
      if (!state.audioplayer || !state.isPlaying) return false;
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
    },

    async resumeAudio({ state, commit, dispatch }, seekTime) {
      if (!state.audioplayer) throw new Error('Аудиоплеер не инициализирован');
      const validatedTime = typeof seekTime === 'number' && !isNaN(seekTime)
        ? Math.max(0, seekTime)
        : state.currentTime;
      commit('SET_CURRENT_TIME', validatedTime);
      dispatch('activeCurrentPodcats');
    },

    async fowardToPlace({ state, dispatch }, { time, idPodcast }) {
      if (idPodcast !== state.activePodcastId) {
        await dispatch('playNewPodcast', idPodcast);
        state.audioplayer.currentTime = time;
      } else {
        state.audioplayer.currentTime = time;
        if (!state.isPlaying) dispatch('togglePlayAudioStatus', true);
      }
    },

    async addPodcastToList({ commit }, podcast) {
      commit('SET_NEW_AUDIO', podcast);
    },

    async toGetNewPodcastsData({ commit }, exclude) {
      try {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', true);
        commit('CLEAR_OTHER_LIST');
        const data = await getOtherPodcastList(exclude);
        data.results.forEach(item => {
          commit('SET_OTHER_LIST', {
            id: item.id,
            object_id: item.object_id,
            audio_length: item.audio_length,
            comments_count: item.comments_count,
            title: item.title,
            date: item.date,
          });
        });
      } catch (error) {
        console.error(`Ошибка при получении подкастов: ${error}`);
      } finally {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', false);
      }
    },
  },

  mutations: {
    SET_AUDIOPLAYER_API(state) {
      state.audioplayer = new Audio();

      state.audioplayer.addEventListener('timeupdate', () => {
        if (!state.isPlaying) return;

        state.currentTime = state.audioplayer.currentTime;

        if (state.activePodcastId) {
          localStorage.setItem('last_audio_id', state.activePodcastId);
          localStorage.setItem('last_remember_time', state.currentTime);
        }
      });

      state.audioplayer.addEventListener('loadstart', () => {
        state.isLoading = true;
      });

      state.audioplayer.addEventListener('canplay', () => {
        state.isLoading = false;
      });

      state.audioplayer.addEventListener('progress', () => {
        const buffered = state.audioplayer.buffered;
        const ranges = [];

        for (let i = 0; i < buffered.length; i++) {
          ranges.push({
            start: buffered.start(i),
            end: buffered.end(i),
          });
        }

        state.bufferedRanges = ranges;
      });
    },

    UPDATE_PODCAST_SIGNED_URL(state, { id, signed_url, expires_at }) {
      state.list[id].signed_url = signed_url;
      state.list[id].expires_at = expires_at;
    },

    SET_VOLUME(state, volume) {
      const validatedVolume = Number(volume);
      state.volume = isNaN(validatedVolume) || validatedVolume < 0 || validatedVolume > 1 ? 0.5 : validatedVolume;
      if (state.audioplayer) state.audioplayer.volume = state.volume;
    },

    SET_SPEED(state, speed) {
      const validatedSpeed = Number(speed);
      state.speed = isNaN(validatedSpeed) || validatedSpeed <= 0 || validatedSpeed > 4 ? 1 : validatedSpeed;
      if (state.audioplayer) state.audioplayer.playbackRate = state.speed;
    },

    SET_AUDIO_URL(state, url) {
      state.audioplayer.src = url;
    },

    TOGGLE_STICKY_PANEL(state, status) {
      state.isStickyPanelOpened = status;
    },

    WRITE_ID_ACTIVE_PODCAST(state, id) {
      state.activePodcastId = id;
    },

    SET_NEW_AUDIO(state, podcast) {
      state.list[podcast.id] = podcast;
    },

    SET_READY_AUDIOPLAYER_FLAG(state) {
      state.isAudioplayerReady = true;
    },

    SET_PREVIEW(state, text) {
      state.podcastPreviewText = text;
    },

    SET_LOADING_AUDIOPLAYER_FLAG(state, status) {
      state.isAudioplayerLoading = status;
    },

    SET_LOADING_AUDIO_FLAG(state, status) {
      state.isLoading = status;
    },

    SET_PLAYING_FLAG(state, status) {
      state.isPlaying = status;
    },

    SET_AUDIO_REQUEST(state, request) {
      state.currentAudioRequest = request;
    },

    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },

    SET_OTHER_LIST(state, data) {
      state.otherPodcasts[data.id] = data;
    },

    SET_LOADING_PODCASTS_LIST_STATUS(state, status) {
      state.isLoadingPodcastsList = status;
    },

    CLEAR_OTHER_LIST(state){
      state.otherPodcasts = {};
    },
  },

  getters: {},
};

export default Audioplayer;